import { AudioState } from "@shared/types";
import { css, htmlArray } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";

const styles: string = css`
    :host {
        display: block;
        width: 200px;
        height: 200px;
        position: absolute;
        left: 0;
        top: 0;

        z-index: 5;
    }

    .audio-container {
        padding: 20px;
        color: white;

        > .audio-button {
            border-radius: 100%;
            background-color: rgb(35, 35, 35);
            height: 50px;
            aspect-ratio: 1/1;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
            transition: background-color 0.3s ease;

            > svg {
                width: 25px;
                height: 25px;
                fill: white;
            }
        }

        > .audio-button:hover {
            background-color: rgb(50, 50, 50);
        }
    }
`;

export class AudioComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current audio state */
    private _currentAudioState: AudioState | undefined;

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        void this.refreshAudioState();
    }

    private async refreshAudioState(): Promise<void> {
        const audioState: AudioState = await this._gameRouteService.getAudioState();
        this._currentAudioState = audioState;

        this.render();
    }

    private volumeMuteIcon: string = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-volume-mute-fill" viewBox="0 0 16 16">
            <path d="M6.717 3.55A.5.5 0 0 1 7 4v8a.5.5 0 0 1-.812.39L3.825 10.5H1.5A.5.5 0 0 1 1 10V6a.5.5 0 0 1 .5-.5h2.325l2.363-1.89a.5.5 0 0 1 .529-.06m7.137 2.096a.5.5 0 0 1 0 .708L12.207 8l1.647 1.646a.5.5 0 0 1-.708.708L11.5 8.707l-1.646 1.647a.5.5 0 0 1-.708-.708L10.793 8 9.146 6.354a.5.5 0 1 1 .708-.708L11.5 7.293l1.646-1.647a.5.5 0 0 1 .708 0"/>
        </svg>
    `;

    private volumeLowIcon: string = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-volume-down-fill" viewBox="0 0 16 16">
            <path d="M9 4a.5.5 0 0 0-.812-.39L5.825 5.5H3.5A.5.5 0 0 0 3 6v4a.5.5 0 0 0 .5.5h2.325l2.363 1.89A.5.5 0 0 0 9 12zm3.025 4a4.5 4.5 0 0 1-1.318 3.182L10 10.475A3.5 3.5 0 0 0 11.025 8 3.5 3.5 0 0 0 10 5.525l.707-.707A4.5 4.5 0 0 1 12.025 8"/>
        </svg>
    `;

    private volumeHighIcon: string = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-volume-up-fill" viewBox="0 0 16 16">
            <path d="M11.536 14.01A8.47 8.47 0 0 0 14.026 8a8.47 8.47 0 0 0-2.49-6.01l-.708.707A7.48 7.48 0 0 1 13.025 8c0 2.071-.84 3.946-2.197 5.303z"/>
            <path d="M10.121 12.596A6.48 6.48 0 0 0 12.025 8a6.48 6.48 0 0 0-1.904-4.596l-.707.707A5.48 5.48 0 0 1 11.025 8a5.48 5.48 0 0 1-1.61 3.89z"/>
            <path d="M8.707 11.182A4.5 4.5 0 0 0 10.025 8a4.5 4.5 0 0 0-1.318-3.182L8 5.525A3.5 3.5 0 0 1 9.025 8 3.5 3.5 0 0 1 8 10.475zM6.717 3.55A.5.5 0 0 1 7 4v8a.5.5 0 0 1-.812.39L3.825 10.5H1.5A.5.5 0 0 1 1 10V6a.5.5 0 0 1 .5-.5h2.325l2.363-1.89a.5.5 0 0 1 .529-.06"/>
        </svg>
    `;

    private render(): void {
        if (!this.shadowRoot) {
            return;
        }
        const element: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderAudioPanel()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...element);

        this.enableFunctionality();
    }

    private renderAudioPanel(): HTMLElement {
        const audioContainer: HTMLElement = document.createElement("div");
        audioContainer.classList.add("audio-container");

        audioContainer.innerHTML = `
            <div class="audio-button">
                ${this.renderVolumeIcon()}
            </div>
        `;

        return audioContainer;
    }

    private renderVolumeIcon(): string {
        if (this._currentAudioState?.muted === true) {
            return this.volumeMuteIcon;
        }
        else {
            if ((this._currentAudioState?.volume ?? 0) < 0.5) {
                return this.volumeLowIcon;
            }
            else {
                return this.volumeHighIcon;
            }
        }
    }

    private async toggleMute(): Promise<void> {
        const isMuted: boolean = this._currentAudioState?.muted ?? false;

        if (isMuted) {
            await this._gameRouteService.executeAction("unmute");
        }
        else {
            await this._gameRouteService.executeAction("mute");
        }
        void this.refreshAudioState();
    }

    private enableFunctionality(): void {
        const audioButton: HTMLElement | null = this.shadowRoot!.querySelector(".audio-button");

        if (audioButton) {
            audioButton.addEventListener("click", async () => {
                await this.toggleMute();
                void this.refreshAudioState();
            });
        }
    }
}
